import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { ProviderPriceListItemModel } from '@models/types'
import type { AppDispatch } from '@redux/store'
import type {
  Action, DeleteParams, EntityOptions, Errors, FetchRequestPayload, RootReducerState, UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const FETCH_REQUEST = 'realbase/providerPriceListItem/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/providerPriceListItem/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/providerPriceListItem/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/providerPriceListItem/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/providerPriceListItem/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/providerPriceListItem/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/providerPriceListItem/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/providerPriceListItem/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/providerPriceListItem/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/providerPriceListItem/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/providerPriceListItem/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/providerPriceListItem/DELETE_FAILURE'

export type ProviderPriceListItemModuleState = {
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: ProviderPriceListItemModuleState = {
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadProviderPriceListItems(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().providerPriceListItems.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/provider_price_list_items.json', options)
      .then(({ data }: { data: ProviderPriceListItemModel[] }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM_ARRAY)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadProviderPriceListItem(providerPriceListItemId: number, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.providerPriceListItems.loadedForKeys]
    const loadedIds = [...state.providerPriceListItems.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)

    if (providerPriceListItemId && !loadedIds.includes(providerPriceListItemId)){
      loadedIds.push(providerPriceListItemId)
    }

    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/provider_price_list_items/${providerPriceListItemId}.json`, options)
      .then(({ data }: { data: ProviderPriceListItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadAgencyPrice(
  providerPriceListItemId: number,
  options: EntityOptions = {},
) {
  return (dispatch: AppDispatch) => {
    dispatch(fetchRequest())

    const promise = api(`/provider_price_list_items/${providerPriceListItemId}/agency_price.json`, options)
      .then(({ data }: { data: { price: number, md5: string } }) => {
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function buildProviderPriceListItemOptions(providerPriceListItemId: number, options: EntityOptions = {}) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    dispatch(fetchRequest())

    // Remove any existing options from redux
    Object.values(getState().entities.providerPriceListItemOptions).forEach((option) => {
      if (option.provider_price_list_item_id === providerPriceListItemId){
        const normalizedOptionJson = normalize(option, Schemas.PROVIDER_PRICE_LIST_ITEM_OPTION)
        dispatch(deleteEntity(normalizedOptionJson))
      }
    })

    const promise = api(`/provider_price_list_items/${providerPriceListItemId}/build_item_options.json`, options)
      .then(({ data }: { data: ProviderPriceListItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createProviderPriceListItem(
  providerPriceListItem: Partial<ProviderPriceListItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      provider_price_list_item: providerPriceListItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/provider_price_list_items.json', options, config)
      .then(({ data }: { data: ProviderPriceListItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateProviderPriceListItem(
  providerPriceListItem: UpdateParams<ProviderPriceListItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      provider_price_list_item: providerPriceListItem,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_list_items/${providerPriceListItem.id}.json`, options, config)
      .then(({ data }: { data: ProviderPriceListItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function duplicateProviderPriceListItem(
  providerPriceListItem: UpdateParams<ProviderPriceListItemModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/provider_price_list_items/${providerPriceListItem.id}/duplicate.json`, options, config)
      .then(({ data }: { data: ProviderPriceListItemModel }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM)
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteProviderPriceListItem(providerPriceListItem: DeleteParams<ProviderPriceListItemModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/provider_price_list_items/${providerPriceListItem.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.PROVIDER_PRICE_LIST_ITEM)
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

// Reducer
export default function reducer(
  state: ProviderPriceListItemModuleState = initialState,
  action: Action = { type: '' },
): ProviderPriceListItemModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
